import React, {
  useState,
  useEffect,
  SetStateAction,
  Dispatch,
  useRef,
  useCallback
} from 'react';
import {
  Menu,
  MenuItem,
  TextField,
  Box,
  Pagination,
  Select,
  MenuItem as SelectMenuItem,
  Typography,
  useTheme,
  CircularProgress,
  Button,
  Stack,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Divider,
  Grid,
  alpha,
  ListItemText,
  LinearProgress
} from '@mui/material';
import {v4 as uuidv4} from 'uuid';
import {InputAdornment} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ModeIcon from '@mui/icons-material/Mode';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import FileThumbnail from 'src/minimal-components/file-thumbnail/file-thumbnail';
import {useAppContext} from '@context/AppContext';
import {useModuleContext} from '@context/ModuleContext';
import {useSnackbar} from 'notistack';
import {BASE_RECORD_ID, BASE_MENU_ITEM} from 'src/routes/BaseRoute';
import AuthUtils from 'src/utils/AuthUtils';
import EncryptUtils from 'src/utils/EncryptUtils';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import FormConstants from '@shared/constants/FormConstants';
import {jwtDecode} from 'jwt-decode';
import {
  fileCabinetUploadData,
  getDocumentsFilecabinetHierarchyData,
  getFilesContent,
  postUploadData,
  saveFileCabinetDocumentData,
  deleteFileCabinetDocumentData,
  editFileFolder,
  createFileFolder,
  permissionsCheck,
  getSearchResultsData
} from '@shared/services/service-handlers/PrivateService';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {
  fileData,
  fileDatas,
  fileThumb,
  fileTypeByUrl
} from 'src/minimal-components/file-thumbnail';
import {AnimatePresence, m} from 'framer-motion';
import {varFade} from '@components/animate';
import Iconify from '@components/iconify';
import {CustomFile} from '@components/upload';
import { s } from '@fullcalendar/core/internal-common';

interface CustomDocViewerProps {
  anchorEl: any;
  children?: any[];
  childrensCount: number;
  // eslint-disable-next-line react/boolean-prop-naming
  open: boolean;
  handleMenuSelect: (data: any) => void;
  handleClose: (data: string[]) => void;
  setDocumentFolders: (data: any[]) => void;
  updateDocumentFolders: (updater: (folders: any[]) => any[] | void) => void;
  setSelectedFolder: (data: any) => void;
  selectedFolder: any;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  documentFolders: any[];
  currentFolder?: any;
  setDocData: Dispatch<SetStateAction<any>>;
  subModuleUIData: any;
  subModuleData: any;
  module: string;
  sub_module: string;
  patient_id: string | any;
  legacyApp?: string;
  unique_id?: string;
  procedure_type?: string;
  accession_number?: string;
  specimen_number_id?: string;
  ParentLegacyValue?: string;
  folder_id?: string;
  date_range?: object;
  rowData?: any;
  height?: any;
  top?: string;
}

type ActionDialogMode =
  | 'create-folder'
  | 'create-file'
  | 'edit'
  | 'tag'
  | 'delete'
  | null;

interface UploadDocumentFormState {
  documentDate: string;
  owner: string;
  verifiedBy: string;
  reviewedBy: string;
  signedBy: string;
  uploadedBy: string;
  status: string;
  documentIdentifier1: string;
  documentIdentifier2: string;
  documentIdentifier3: string;
  documentIdentifier4: string;
  documentIdentifier5: string;
  files: any[];
}

const getTodayDate = (): string => {
  const today = new Date();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${today.getFullYear()}-${month}-${day}`;
};

const createDefaultUploadForm = (): UploadDocumentFormState => ({
  documentDate: getTodayDate(),
  owner: '',
  verifiedBy: '',
  reviewedBy: '',
  signedBy: '',
  uploadedBy: '',
  status: 'Draft',
  documentIdentifier1: '',
  documentIdentifier2: '',
  documentIdentifier3: '',
  documentIdentifier4: '',
  documentIdentifier5: '',
  files: []
});

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const parseFileExtensions = (rawExtensions: string | string[] | null) => {
  let fileExtensions: string[] = [];

  if (Array.isArray(rawExtensions)) {
    fileExtensions = rawExtensions;
  } else if (typeof rawExtensions === 'string') {
    try {
      const parsed = JSON.parse(rawExtensions);
      if (Array.isArray(parsed)) {
        fileExtensions = parsed;
      } else {
        fileExtensions = rawExtensions.split(',').map((e: string) => e.trim());
      }
    } catch {
      if (rawExtensions.startsWith('[') && rawExtensions.endsWith(']')) {
        const inner = rawExtensions.slice(1, -1);
        fileExtensions = inner
          .split(',')
          .map(e => e.trim().replace(/^"|"$/g, ''));
      } else {
        fileExtensions = rawExtensions.split(',').map(e => e.trim());
      }
    }
  }

  return fileExtensions.filter(Boolean);
};
const getFileFormatFromType = (type: string) => {
  if (!type) return null; // Handle undefined or null input
  return type.split('/').pop(); // Split by '/' and return the last part
};
const getNodeType = (item: any) => {
  const fileExtensions = parseFileExtensions(item?.FileExtensions ?? null);
  const fileType =
    fileExtensions.length > 0 ? fileExtensions[0]?.toLowerCase() : '';

  return !fileExtensions.length
    ? 'folder'
    : fileExtensions.length > 1
      ? 'multi'
      : fileType;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const MenuWithSearch = ({
  anchorEl,
  open,
  handleClose,
  children = [],
  childrensCount,
  handleMenuSelect,
  documentFolders,
  setDocumentFolders,
  updateDocumentFolders,
  subModuleUIData,
  rowData,
  date_range,
  ParentLegacyValue,
  module,
  sub_module,
  patient_id,
  legacyApp,
  unique_id,
  selectedFolder,
  setSelectedFolder,
  currentFolder,
  setDocData,
  defaultPageSize = 10,
  pageSizeOptions = [5, 10, 20]
}: CustomDocViewerProps) => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [searching, setSearching] = useState(false);
  const latestRequestIdRef = useRef(0);
  const browseFilesInputRef = useRef<HTMLInputElement | null>(null);
  const [dialogMode, setDialogMode] = useState<ActionDialogMode>(null);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [nameInput, setNameInput] = useState('');
  const [tagsInput, setTagsInput] = useState('');
  const [uploadFormErrors, setUploadFormErrors] = useState({
    documentDate: false,
    documentIdentifier1: false,
  });
  const [uploadForm, setUploadForm] = useState<UploadDocumentFormState>(
    createDefaultUploadForm()
  );
  const [uploadedfiles, setUploadedFiles] = useState<any[]>([]);
  const [deleteLoader, setDeleteLoader] = useState(false);
  const debouncedValue = useDebounce(search, 500);
  const [uploadProgress, setUploadProgress] = useState<any[]>([]);
  const [permissionsResponse, setPermissionsResponse] = useState<any>(null);
  useEffect(() => {
    setSearch('');
    setPage(1);
    setPageSize(defaultPageSize);
    setSearching(false);
    setDialogMode(null);
    setActiveItem(null);
    setNameInput('');
    setTagsInput('');
    setUploadFormErrors({documentDate: false, documentIdentifier1: false});
    setUploadForm(createDefaultUploadForm());
  }, [anchorEl, defaultPageSize]);

  useEffect(() => {
    const fetchPermissions = async () => {
      try {
        const payload = {};
        const encryptedPayload = EncryptUtils.encryptedData(payload);
        const response = await permissionsCheck(
          'FileCabinet',
          'module',
          {tk: encryptedPayload},
          'checkPermissions'
        );
        setPermissionsResponse(response);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };
    fetchPermissions();
  }, []);


  // Extract permissions from response
  const canCreateFolder =
    permissionsResponse?.data?.some(
      (p: any) => p?.function === 'Create-Folder'
    ) || false;
  const canCreateFile =
    permissionsResponse?.data?.some(
      (p: any) => p?.function === 'Update-File'
    ) || false;
  const Actions =
    permissionsResponse?.data?.some(
      (p: any) => p?.function === 'Rename-Edit-Delete-Actions'
    ) || false;

  if (AuthUtils.getAuthToken()) {
    var currentUser = jwtDecode(AuthUtils.getAuthToken() || '') as any;
  }
  const {
    appState: {applicationName}
  } = useAppContext();
  const {moduleState} = useModuleContext();
  const {isBH, isBHReason}: any = moduleState;
  const recordId = EncryptUtils.getDecryptedData(
    location?.search,
    BASE_RECORD_ID
  );

  const menuItem = new URLSearchParams(location?.search).get(BASE_MENU_ITEM);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {enqueueSnackbar} = useSnackbar();
  const getLocaleStorageData = AuthUtils.getSelectedCheckBoxData();
  // Filter children based on search text (case-insensitive)
  const {
    document_folders_sql: documentFolderSql,
    document_folders_count_sql: documentFolderCountSql,
    legit_documents_folders_sql: legitDocumentsFoldersSql,
    document_folders_contents: folderContentsQuery,
    filter_schema_clause: filterSchemaClause,
    variables
  } = subModuleUIData;
  const getDocumentFolders = async (
    requestId: number,
    limit?: number,
    offset?: number
  ) => {
    setIsLoading(true);
    const currentRequestId = requestId ?? latestRequestIdRef.current;

    try {
      const sql = documentFolderSql
        ? {
            documentFolderSql: `${documentFolderSql}`,
            legitDocumentFoldersSql: `${legitDocumentsFoldersSql}`,
            documentFolderCountSql: `${documentFolderCountSql}`
          }
        : {
            folderContentsQuery
          };
      const auditObj = {
        is_audit: true,
        audit_event_description: FormConstants.COMMON.VIEWED_DOCUMENT,
        rowData: rowData
      };
      let dateRanges;
      const tempDataColumns: any = {};
      const sqlData = {
        // eslint-disable-next-line max-len
        sql: subModuleUIData?.legacy_app_dropdown_sql,
        variables: [{}],
        order_clause_not_required: true,
        schema: subModuleUIData?.filter_schema_clause,
        filter_schema_clause: subModuleUIData?.filter_schema_clause
      };
      const safeFolderName = (legacyApp || ParentLegacyValue)?.replace(
        /'/g,
        "''"
      );
      sqlData.sql = subModuleUIData?.minMax_date_sql
        ?.replaceAll('###LegacyApp', `'${legacyApp || ParentLegacyValue}'`)
        .replaceAll('###FolderName', `'${safeFolderName}'`);
      const datedata: any = {
        sql_data: sqlData,
        add_document_security_ids: true,

        dropdown_key: 'Folder'
      };
      const encryptRequestDateData = EncryptUtils.encryptedData(datedata);
      const result = await getSearchResultsData(applicationName, module || '', {
        tk: encryptRequestDateData
      });
      if (
        result?.data[0]?.fromdate === null ||
        result?.data[0]?.todate === null
      ) {
        dateRanges = date_range;
      } else {
        dateRanges = {
          fromDate: result?.data[0]?.fromdate || null,
          toDate: result?.data[0]?.todate || null
        };
      }
      if (rowData) {
        const tempObj = rowData;
        for (const key in tempObj) {
          if (Object.hasOwn(tempObj, key)) {
            tempDataColumns[key] = tempObj[key];
          }
        }
      }

      const data: any = {
        sql: sql,
        variables: variables,
        filterSchemaClause: filterSchemaClause,
        Folder_ID: selectedFolder?.Folder_ID,
        date_range: dateRanges ?? {},
        limit: limit || defaultPageSize,
        offset: offset || 0,
        audit_identifier: AuthUtils.getAuditIdentifier(),
        audit_identifier_type: AuthUtils.getAuditIdentifierType(),
        audit_legacy_app: AuthUtils.getAuditLegacyApp(),
        isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
        isBH_reason: isBHReason,
        application_name: applicationName,
        FileName: search,
        legacyMRN: recordId
      };

      data.sql_data = {...auditObj, ...tempDataColumns};

      if (ParentLegacyValue) {
        data.parentLegacyValue = ParentLegacyValue;
      }
      if (subModuleUIData.filter_schema_clause) {
        data.filter_schema_clause = subModuleUIData.filter_schema_clause;
      }
      if (
        subModuleUIData?.is_filter_sql_required &&
        getLocaleStorageData.length > 0
      ) {
        data.filter_data = getLocaleStorageData;
      }
      const encryptRequestData = EncryptUtils.encryptedData(data);

      const response = documentFolderSql
        ? await getDocumentsFilecabinetHierarchyData(
            module,
            sub_module,
            patient_id,
            {tk: encryptRequestData},
            legacyApp,
            unique_id,
            menuItem ?? ''
          )
        : await getFilesContent(
            module,
            sub_module,
            patient_id,
            unique_id || '',
            {tk: encryptRequestData},
            legacyApp,
            menuItem ?? ''
          );

      const {
        documents_hierarchy: documentsHierarchy,
        documents: documents,
        count: count
      } = response;

      if (currentRequestId !== latestRequestIdRef.current) {
        return;
      }

      const addChildToFolder = (
        id: number,
        childData: any[],
        countData: any[]
      ) => {
        const clonedTree = JSON.parse(JSON.stringify(documentFolders));
        const stack = [...clonedTree];

        while (stack.length > 0) {
          const current = stack.pop();

          if (current.ID === id) {
            current.ChildNodes = [...childData];
            current.COUNTS = countData?.[0]?.counts ?? childData.length;
            break;
          }

          if (Array.isArray(current.ChildNodes)) {
            stack.push(...current.ChildNodes);
          }
        }

        setDocumentFolders(clonedTree);
      };

      addChildToFolder(
        selectedFolder?.Folder_ID,
        documentsHierarchy ? documentsHierarchy : documents,
        count
      );

      setSelectedFolder((prevState: any) => ({
        ...prevState,
        isLoaded: false
      }));
      setIsLoading(false);
    } catch (e) {
      if (currentRequestId !== latestRequestIdRef.current) {
        return;
      }

      const errMessage = getErrorMessage(e);
      if (Array.isArray(documentFolders) && documentFolders.length === 0) {
        setIsLoading(false);
      } else {
        enqueueSnackbar(errMessage, SnackBarConfig.getError());
      }

      setSelectedFolder((prevState: any) => ({
        ...prevState,
        isLoaded: false
      }));
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedFolder?.Folder_ID && selectedFolder?.isLoaded) {
      const newRequestId = Date.now();
      latestRequestIdRef.current = newRequestId;
      getDocumentFolders(newRequestId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder]);

  const pageCount = Math.ceil(childrensCount / pageSize);
  const pagedChildren = childrensCount > 0 ? children : [];

  const handlePageChange = (_: any, value: React.SetStateAction<number>) => {
    setPage(value);
    const newRequestId = Date.now();
    latestRequestIdRef.current = newRequestId;
    getDocumentFolders(newRequestId, pageSize, pageSize * (Number(value) - 1));
  };

  const handlePageSizeChange = (e: {target: {value: any}}) => {
    setPageSize(Number(e.target.value));
    setPage(1);
    const newRequestId = Date.now();
    latestRequestIdRef.current = newRequestId;
    getDocumentFolders(newRequestId, Number(e.target.value), 0);
  };

  const handleSearch = async () => {
    setPage(1);
    const newRequestId = Date.now();
    latestRequestIdRef.current = newRequestId;
    await getDocumentFolders(newRequestId, pageSize);
  };

  useEffect(() => {
    if (searching) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const updateCurrentFolderChildren = (
    updater: (childrenList: any[], folderNode: any) => any[]
  ) => {
    const folderId = currentFolder?.ID;

    if (!folderId) {
      return;
    }

    updateDocumentFolders((folders: any[]) => {
      const stack = [...folders];

      while (stack.length > 0) {
        const current = stack.pop();

        if (current.ID === folderId) {
          const existingChildren = Array.isArray(current.ChildNodes)
            ? current.ChildNodes
            : [];
          current.ChildNodes = updater(existingChildren, current);
          current.COUNTS = current.ChildNodes.length;
          break;
        }

        if (Array.isArray(current.ChildNodes)) {
          stack.push(...current.ChildNodes);
        }
      }

      return folders;
    });
  };

  const findFolderNodeById = (folders: any[], nodeId: any) => {
    if (!Array.isArray(folders) || !nodeId) {
      return null;
    }

    const stack = [...folders];

    while (stack.length > 0) {
      const current = stack.pop();

      if (current?.ID === nodeId) {
        return current;
      }

      if (Array.isArray(current?.ChildNodes)) {
        stack.push(...current.ChildNodes);
      }
    }

    return null;
  };

const getMetadataValue = (...values: any[]) =>
  values.find(
    value =>
      value !== undefined &&
      value !== null &&
      !(typeof value === 'string' && value.trim() === '')
  ) ?? '';

function extractIdValue(rawIdValue) {
  let idValue = rawIdValue;

  if (Array.isArray(rawIdValue)) {
    idValue = rawIdValue[0];
  } else if (typeof rawIdValue === 'string') {
    const trimmedIdValue = rawIdValue.trim();

    if (trimmedIdValue.startsWith('[') && trimmedIdValue.endsWith(']')) {
      try {
        const parsedIdValue = JSON.parse(trimmedIdValue);
        idValue = Array.isArray(parsedIdValue)
          ? parsedIdValue[0]
          : parsedIdValue;
      } catch {
        idValue = trimmedIdValue
          .slice(1, -1)
          .split(',')
          .map((value) => value.trim())
          .filter(Boolean)[0];
      }
    } else {
      idValue = trimmedIdValue;
    }
  }

  return idValue;
}

const getUploadFileMeta = (file: any) => {
  if (!file) {
    return null;
  }

  if (file instanceof File) {
    return {
      id: file.name,
      fileup: file,
      key: '',
      name: file.name,
      originalname: file.name,
      size: file.size ? file.size / 1024 / 1024 : 0,
      path: '',
      type: file.type || '',
      endName: fileTypeByUrl(file.name),
      preview: '',
      lastModified: file.lastModified
    };
  }

  const meta = fileDatas(file);

  if (!meta) {
    return null;
  }

  return {
    id: meta.id || meta.name || '',
    fileup: meta.fileup || null,
    key: meta.key || '',
    name: meta.name || '',
    originalname: meta.originalname || meta.name || '',
    size: meta.size || 0,
    path: meta.path || '',
    type: meta.type || '',
    endName: meta.endName || fileTypeByUrl(meta.name || ''),
    preview: meta.preview || '',
    lastModified: meta.lastModified
  };
};

  const buildLocalNode = (name: string, isFolder: boolean) => {
    const trimmedName = name.trim();
    const extension = trimmedName.includes('.')
      ? trimmedName.split('.').pop()?.toLowerCase() || 'txt'
      : 'txt';

    return {
      ID: `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      Name: trimmedName,
      LegacyApp: currentFolder?.LegacyApp || '',
      LegacyMRN: currentFolder?.LegacyMRN || '',
      SourceDocumentID: '',
      ParentDocumentTypeID: currentFolder?.DocumentTypeID ?? null,
      FileExtensions: isFolder ? null : [extension],
      IsNotes: [],
      Description: '',
      IsActive: true,
      FULL_PATH: '',
      ALPHA_SEQ_PATH: '',
      order: 0,
      IsMultiPage: 'false',
      Sequence: [],
      DocumentTypeID: currentFolder?.DocumentTypeID ?? 0,
      ChildNodes: isFolder ? [] : undefined,
      COUNTS: 0,
      HasDocuments: false,
      localTags: []
    };
  };
  
const formatDateForInput = (dateString: string) => {
  if (!dateString) return '';
  return new Date(dateString).toISOString().split('T')[0];
};

const getMetadataFormState = (  
  item: any
): UploadDocumentFormState => (
  {
  documentDate: formatDateForInput(
    getMetadataValue(
      item?.documentDate,
      item?.DocumentDate,
      getTodayDate()
    )
  ),

  owner: getMetadataValue(item?.owner, item?.Owner),

  verifiedBy: getMetadataValue(
    item?.VerfiedBy,
    item?.VerifiedBy
  ),

  reviewedBy: getMetadataValue(
    item?.reviewedBy,
    item?.ReviewedBy
  ),

  signedBy: getMetadataValue(
    item?.signedBy,
    item?.SignedBy
  ),

  uploadedBy: getMetadataValue(
    item?.uploadedBy,
    item?.UploadedBy
  ),

  status: getMetadataValue(item?.status, item?.Status, 'Draft'),

  documentIdentifier1: getMetadataValue(item?.Index1, item?.documentIdentifier1),
  documentIdentifier2: getMetadataValue(item?.Index2, item?.documentIdentifier2),
  documentIdentifier3: getMetadataValue(item?.Index3, item?.documentIdentifier3),
  documentIdentifier4: getMetadataValue(item?.Index4, item?.documentIdentifier4),
  documentIdentifier5: getMetadataValue(item?.Index5, item?.documentIdentifier5),

  files: []
});

  const openDialog = (mode: ActionDialogMode, item?: any) => {
    setDialogMode(mode);
    setActiveItem(item || null);
    setNameInput(item?.Name || '');
    setTagsInput(
      Array.isArray(item?.localTags) ? item.localTags.join(', ') : ''
    );
    setUploadFormErrors({documentDate: false, documentIdentifier1: false});
    // setUploadForm(createDefaultUploadForm());
        // setUploadForm(
    //   mode === 'tag'
    //     ? getMetadataFormState(item || {}, docData || {})
    //     : createDefaultUploadForm()
    // );
    
if (mode === 'tag' && item?.ID) {
  const idValue = extractIdValue(item?.ID);
  fetchMetadataById(idValue);
} else {
  setUploadForm(createDefaultUploadForm());
}

    setUploadedFiles([]);
    setUploadProgress([]);
  };

const fetchMetadataById = async (id: any) => {
  try {
    setIsLoading(true);

    const payload = {
      ID: id,
    };

    const encryptedPayload = EncryptUtils.encryptedData(payload);

    const response = await editFileFolder(
          'FileCabinet',
          'module',
          {tk: encryptedPayload},
          'getMetadataById'
        );

    const rawData = response?.data?.[0]?.[0] || {};

    const formattedData = {
      ...rawData,
      DocumentDate: rawData?.DocumentDate
        ? new Date(rawData.DocumentDate).toISOString().split('T')[0]
        : ''
    };

    setUploadForm(getMetadataFormState(formattedData));


    setIsLoading(false);
  } catch (error) {
    console.error(error);
    enqueueSnackbar('Failed to fetch metadata', SnackBarConfig.getError());
    setIsLoading(false);
  }
};

  const closeDialog = () => {
    setDialogMode(null);
    setActiveItem(null);
    setNameInput('');
    setTagsInput('');
    setUploadFormErrors({documentDate: false, documentIdentifier1: false});
    setUploadForm(createDefaultUploadForm());
    setUploadedFiles([]);
    setUploadProgress([]);
    if (browseFilesInputRef.current) {
      browseFilesInputRef.current.value = '';
    }
  };

  const handleUploadFormChange =
    (field: keyof UploadDocumentFormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;

      setUploadForm(prev => ({
        ...prev,
        [field]: value
      }));

      if (field === 'documentDate') {
        setUploadFormErrors(prev => ({
          ...prev,
          documentDate: !value
        }));
      }
      if (field === 'documentIdentifier1') {
        setUploadFormErrors(prev => ({
          ...prev,
          documentIdentifier1: !value.trim()
        }));
      }
    };

  const handleRemoveSelectedFile = (fileIndex: number): void => {
    const fileToRemove = uploadForm.files[fileIndex];
    const fileMeta = getUploadFileMeta(fileToRemove) || {id: ''};
    const removeId = fileMeta.id;

    setUploadForm(prev => ({
      ...prev,
      files: prev.files.filter((_, index) => index !== fileIndex)
    }));
    setUploadProgress(prev =>
      prev.filter(progressItem => progressItem.id !== removeId)
    );
    setUploadedFiles(prev =>
      prev.filter((uploadedItem: any) => uploadedItem?.id !== removeId)
    );
  };
  const uploadFileToServer = async (file: any) => {
    try {
      const fileMeta = getUploadFileMeta(file);

      if (!fileMeta?.fileup || !fileMeta.originalname) {
        enqueueSnackbar(
          'Selected item is not a valid file. Please choose a file and try again.',
          SnackBarConfig.getWarning()
        );
        return null;
      }

      const {id, fileup, originalname} = fileMeta || {
        id: '',
        fileup: '',
        originalname: ''
      };
      const uniqueID = uuidv4();
      const reqPayload = EncryptUtils.encryptedData({
        userId: `${uniqueID}-${originalname}`,
        legacyApp: legacyApp || ParentLegacyValue || ''
      });
      const formData = new FormData();
      if (fileup && originalname) {
        formData.append('file', fileup);
        formData.append('tk', reqPayload);
      }
      const response = await fileCabinetUploadData(
        'FileCabinet',
        'module',
        formData,
        'uploadFile',
        progressEvent => {
          if (progressEvent.total) {
            const total = progressEvent.total ?? 0;
            const progress =
              total > 0 ? Math.round((progressEvent.loaded / total) * 100) : 0;

            setUploadProgress((prev: any[]) => {
              const itemExists = prev.some(item => item.id === id);

              if (itemExists) {
                return prev.map(item =>
                  item.id === id
                    ? {...item, progress: progress, display: true}
                    : item
                );
              } else {
                return [...prev, {id, progress: progress, display: true}];
              }
            });
          }
        }
      );
      await setUploadProgress(prev =>
        prev.map(item => (item.id === id ? {...item, display: false} : item))
      );
      return response.data;
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };
  const handleUploadAll = async (
    files: any[] | undefined,
    uploadProgressdata: any[]
  ) => {
    if (files) {
      try {
        let updatedFiles = [...uploadedfiles];
        let insertResult: {data: any[]; module_data: any[]} | undefined =
          undefined;
        for (const file of files) {
          const fileMeta = getUploadFileMeta(file);

          if (!fileMeta?.id || !fileMeta?.fileup) {
            continue;
          }

          const {id, name = '', size = 0, type = ''} = fileMeta;

          const isFileAlreadyUploaded = updatedFiles.some(
            progress => progress?.id === id
          );
          if (!isFileAlreadyUploaded) {
            const blobUrl = await uploadFileToServer(file); // Wait for each file to finish before starting the next

            if (!blobUrl) {
              continue;
            }

            // Update the file object with the blob URL
            const existingFileIndex = updatedFiles.findIndex(
              (item: any) => item?.id === id
            );
            if (existingFileIndex !== -1) {
              // Update the existing file's blob URL
              const existingFile = updatedFiles[existingFileIndex];
              if (existingFile) {
                updatedFiles[existingFileIndex] = {
                  ...existingFile,
                  blobUrl: blobUrl
                };
              }
            } else {
              // Add the new file with its blob URL
              updatedFiles = [
                ...updatedFiles,
                {
                  id: id,
                  name: name,
                  type: type,
                  size: size * 1024 * 1024,
                  blobUrl: blobUrl
                  }
                ];
                setUploadedFiles(updatedFiles);
                setDeleteLoader(true);

                setDeleteLoader(false);
              }
              setUploadedFiles(updatedFiles);
              }
  
              // Update the form value with the updated files array
            }
            setDeleteLoader(false);
          } catch (error) {
          console.error('Error uploading file:', error);
          setDeleteLoader(false);
        }
      }
    }
  const inits = async (files: any) => {
    if (files) {
      const validFiles = files.filter((file: any) => !!getUploadFileMeta(file));

      if (validFiles.length === 0) {
        enqueueSnackbar(
          'No valid files were selected. Please choose one or more files and try again.',
          SnackBarConfig.getWarning()
        );
        return;
      }

      if (validFiles.length !== files.length) {
        enqueueSnackbar(
          'Some selected items were skipped because they are not valid files.',
          SnackBarConfig.getWarning()
        );
      }

        setUploadForm(prev => ({
      ...prev,
      files: validFiles
    }));
        // setUploadedFile((prev: any) => [...prev, ...files]);
        const newFiles = validFiles.filter(
          (file: any) => {
            const fileMeta = getUploadFileMeta(file);
            return (
              !!fileMeta?.id &&
              !uploadProgress.some(progress => progress.id === fileMeta.id)
            );
          }
        );
        const initialProgress = newFiles.map((file: any) => {
          const fileMeta = getUploadFileMeta(file);
          return {
          id: fileMeta?.id || '',
          progress: 0,
          display: true
        };
        }).filter(item => !!item.id);

      await setUploadProgress(prev => {
        const updatedProgress = [...prev, ...initialProgress];
        handleUploadAll(validFiles, updatedProgress);
        return updatedProgress;
      });
    }

    // await setValue('displayprogress', uploadProgress);
  };
  const handleBrowseFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);

    if (selectedFiles.length === 0) {
      enqueueSnackbar(
        'No file was selected. Please choose a file and try again.',
        SnackBarConfig.getWarning()
      );
      return;
    }

    const oldfiles = uploadForm.files;
    const newFiles = await Promise.all(
      selectedFiles.map(async file => {
        const uniqueID = uuidv4();
        return {
          id: uniqueID,
          file,
          preview: ''
        };
      })
    );
    inits([...oldfiles, ...newFiles]);

    if (e.target) {
      e.target.value = '';
    }
  };

  const handleDialogSubmit = async () => {
    const trimmedName = nameInput.trim();

    if (dialogMode === 'create-folder') {
      if (!trimmedName) {
        enqueueSnackbar('Please enter a name.', SnackBarConfig.getWarning());
        return;
      }
      if (!/^[a-zA-Z0-9 _]+$/.test(trimmedName)) {
        enqueueSnackbar('Name can only contain letters, numbers, spaces, and underscores.', SnackBarConfig.getWarning());
        return;
      }
      const itemType = getNodeType(activeItem);
      const type = itemType === 'folder' ? 'folder' : 'file';
      try {
        const payload = {
          type: type,
          FolderName: trimmedName,
          FilecabinetDocumentTypeID:
            activeItem?.ID ||
            selectedFolder?.Folder_ID ||
            currentFolder?.ID ||
            '',
          LegacyApp: legacyApp || ParentLegacyValue || '',
          create_folder_sql: subModuleUIData?.folder_file_sql?.create_folder_sql
        };
        const encryptedPayload = EncryptUtils.encryptedData(payload);
        const response = await createFileFolder(
          'FileCabinet',
          'module',
          {tk: encryptedPayload},
          'createFileFolder'
        );

if(response?.data[0]?.[0]?.Message) {
  enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getWarning());
  return;
}else {
  enqueueSnackbar('Folder created successfully.', SnackBarConfig.getSuccess());
    closeDialog();    
  
      // Refresh the folder contents after successful delete
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
      return;
}
    }catch (error) {
        enqueueSnackbar(
          'Folder created successfully.',
          SnackBarConfig.getSuccess()
        );
        closeDialog();
        return;
      }
    }

    if (dialogMode === 'create-file') {
      if (!uploadForm.documentDate?.trim()) {
        setUploadFormErrors({
          documentDate: true, 
          documentIdentifier1: false
        });
        enqueueSnackbar(
          'Document date is required.',
          SnackBarConfig.getWarning()
        );
        return;
      }

      if (!uploadForm.files || uploadForm.files.length === 0) {
        enqueueSnackbar(
          'Please upload at least one document.',
          SnackBarConfig.getWarning()
        );
        return;
      }

      const validFiles = uploadForm.files
        .map(file => ({
          meta: getUploadFileMeta(file)
        }))
        .filter(
          (
            file
          ): file is {
            meta: NonNullable<ReturnType<typeof getUploadFileMeta>>;
          } => !!file.meta?.fileup
        );

      if (validFiles.length === 0) {
        enqueueSnackbar(
          'No valid files are available to upload. Please select a file and try again.',
          SnackBarConfig.getWarning()
        );
        return;
      }

      if (validFiles.length !== uploadForm.files.length) {
        enqueueSnackbar(
          'Some invalid file entries were skipped. Please review the selected files.',
          SnackBarConfig.getWarning()
        );
      }

      const uploadPayload = {
        legacyApp: legacyApp || ParentLegacyValue || '',
        Folder_ID: selectedFolder?.Folder_ID || currentFolder?.ID || '',
        Name:
          uploadForm.documentIdentifier1 ||
          validFiles[0]?.meta?.name ||
          '',
        ...uploadForm,
        uploadedBy: currentUser?.name || uploadForm.uploadedBy || '',
        SourceDocumentID: '',
        versionNo: 1,
        versionDate: uploadForm.documentDate || '',
        PhysicalName: validFiles[0]?.meta?.name || '',
        documentType: validFiles[0]?.meta?.type || '',
        file_uploading_sql:
          subModuleUIData?.folder_file_sql?.file_uploading_sql,
        counter: validFiles.length,
        files: validFiles.map(({meta: fileMeta}) => {
          const matchedUploadedFile = uploadedfiles.find(
            (uploadedItem: any) => uploadedItem?.id === fileMeta.id
          );

          return {
            id: fileMeta.id,
            fileName: fileMeta.name,
            size: fileMeta.size,
            type: fileMeta.type,
            lastModified: fileMeta.fileup?.lastModified || null,
            name: matchedUploadedFile?.blobUrl || ''
          };
        })
      };

      const missingUploads = uploadPayload.files.filter(file => !file.name);

      if (missingUploads.length > 0) {
        enqueueSnackbar(
          'One or more files are still uploading or failed to upload. Please wait and try again.',
          SnackBarConfig.getWarning()
        );
        return;
      }

      try {
        const encryptedUploadPayload = EncryptUtils.encryptedData(uploadPayload);
    let response = await saveFileCabinetDocumentData(
          'FileCabinet',
          'module',
          {tk: encryptedUploadPayload},
          'saveFileCabinetDocument'
        );
    
       if(response?.data[0]?.[0]?.Success === 0) {
     enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getWarning());
      closeDialog();    
  return;
}else if(response?.data[0]?.[0]?.Success === 1){
  enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getSuccess());
    closeDialog();    
      // Refresh the folder contents after successful delete
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
      return;
}

       } catch (error) {
        enqueueSnackbar(
          getErrorMessage(error),
          SnackBarConfig.getWarning()
        );
        return;
      }
    }
    if (!activeItem) {
      return;
    }
    if (dialogMode === 'edit') {
      // update foleder name or file name based on the type
      if (!trimmedName) {
        enqueueSnackbar('Please enter a name.', SnackBarConfig.getWarning());
        return;
      }
      const itemType = getNodeType(activeItem);
      const editType = itemType === 'folder' ? 'folder' : 'file';
      const isValidEditName =
        editType === 'folder'
          ? /^[a-zA-Z0-9 _]+$/.test(trimmedName)
          : /^[a-zA-Z0-9 _.()\-]+$/.test(trimmedName);
      if (!isValidEditName) {
        enqueueSnackbar(
          editType === 'folder'
            ? 'Folder name can only contain letters, numbers, spaces, and underscores.'
            : 'File name can only contain letters, numbers, spaces, underscores, dots, hyphens, and parentheses.',
          SnackBarConfig.getWarning()
        );
        return;
      }
   if (editType === 'folder') {
          try {
        const payload = {
          editType: editType,
          FolderName:trimmedName,
          FolderID: activeItem?.ID || '',
          update_folder_sql: subModuleUIData?.folder_file_sql?.update_folder_sql
        };
        const encryptedPayload = EncryptUtils.encryptedData(payload);
      await editFileFolder(
          'FileCabinet',
          'module',
          {tk: encryptedPayload},
          'editFileFolder'
        );
        enqueueSnackbar('Folder updated successfully.', SnackBarConfig.getSuccess());
    closeDialog();    
      // Refresh the folder contents after successful delete
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
      return;
    }catch (error) {
        enqueueSnackbar(
        getErrorMessage(error),
        SnackBarConfig.getError()
      );
      return;
    }
  }else if (editType === 'file') { 

     const rawIdValue = activeItem?.ID;
     let idValue = rawIdValue;

     if (Array.isArray(rawIdValue)) {
       idValue = rawIdValue[0];
     } else if (typeof rawIdValue === 'string') {
       const trimmedIdValue = rawIdValue.trim();

       if (trimmedIdValue.startsWith('[') && trimmedIdValue.endsWith(']')) {
         try {
           const parsedIdValue = JSON.parse(trimmedIdValue);
           idValue = Array.isArray(parsedIdValue) ? parsedIdValue[0] : parsedIdValue;
         } catch {
           idValue = trimmedIdValue
             .slice(1, -1)
             .split(',')
             .map((value) => value.trim())
             .filter(Boolean)[0];
         }
       }
     }

          try {
        const payload = {
          editType: editType,
          fileName:trimmedName,
          ID: idValue,
          update_fileName_sql: subModuleUIData?.folder_file_sql?.update_fileName_sql
        };
        const encryptedPayload = EncryptUtils.encryptedData(payload);
       let response =  await editFileFolder(
          'FileCabinet',
          'module',
          {tk: encryptedPayload},
          'editFileName'
        );
        // console.log('edit response==>1356',response)
  if(response?.data[0]?.[0]?.Success === 0) {
  enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getWarning());
    closeDialog();
  return;
  }else if(response?.data[0]?.[0]?.Success === 1){        

        enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getSuccess());
       closeDialog();    
      // Refresh the folder contents after successful 
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
      return;
    }
  }catch (error) {
        enqueueSnackbar(
        getErrorMessage(error),
        SnackBarConfig.getError()
      );
      return;
    }

  }
  return;
    }
    if (dialogMode === 'tag') {
if (!uploadForm.documentIdentifier1?.trim()) {
  setUploadFormErrors(prev => ({
    ...prev,
    documentIdentifier1: true
  }));
  enqueueSnackbar(
    'Document identifier 1 is required.',
    SnackBarConfig.getWarning()
  );
  return;
}
let ID = activeItem?.ID?.replace(/[\[\]]/g, '')

     try {
      const metadataValues = {
        documentDate: uploadForm.documentDate,
        owner: uploadForm.owner,
        verifiedBy: uploadForm.verifiedBy,
        reviewedBy: uploadForm.reviewedBy,
        signedBy: uploadForm.signedBy,
        uploadedBy: currentUser?.name || uploadForm.uploadedBy,
        status: uploadForm.status,
        documentIdentifier1: uploadForm.documentIdentifier1,
        documentIdentifier2: uploadForm.documentIdentifier2,
        documentIdentifier3: uploadForm.documentIdentifier3,
        documentIdentifier4: uploadForm.documentIdentifier4,
        documentIdentifier5: uploadForm.documentIdentifier5,
        ID:ID, 
        itemName: activeItem?.Name,
        update_metadata_sql: subModuleUIData?.folder_file_sql?.update_metadata_sql
      };

      console.log('Metadata form values:', metadataValues) 

        const encryptedpayload = EncryptUtils.encryptedData(metadataValues);
        await saveFileCabinetDocumentData(
          'FileCabinet',
          'module',
          {tk: encryptedpayload},
          'updateMetadata'
        );

        closeDialog();
        // Refresh the folder contents after successful 
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
        enqueueSnackbar(
          'Metadata updated successfully.',
          SnackBarConfig.getSuccess()
        );
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), SnackBarConfig.getError());
      }
      closeDialog();
        return;
    }
    if (dialogMode === 'delete') {
      const itemType = getNodeType(activeItem);
      const deleteType = itemType === 'folder' ? 'folder' : 'file';
      try {
        if (deleteType === 'folder') {

    const deletePayload = {
        legacyApp: legacyApp || ParentLegacyValue || '',
        Folder_ID: activeItem.ID,
        delete_folder_sql:subModuleUIData?.folder_file_sql?.delete_folder_sql
        };

        const encryptedDeletePayload = EncryptUtils.encryptedData(deletePayload);
        let response = await deleteFileCabinetDocumentData(
          'FileCabinet',
          'module',
          {tk: encryptedDeletePayload},
          'deleteFileCabinetFolder'
        );
        
// console.log('delete response==>1497',activeItem.ID,response)
  if(response?.data[0]?.[0]?.Success === 0) {
  enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getWarning());
    closeDialog();    
 
  return;
}else if(response?.data[0]?.[0]?.Success === 1){
  enqueueSnackbar(response.data[0][0].Message, SnackBarConfig.getSuccess());
    closeDialog();    
  
      // Refresh the folder contents after successful delete
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
      return;
}
            
        }
        else if (deleteType === 'file'){
        const deletePayload = {
          delete_type: deleteType,
          Document_ID: activeItem.ID,
          delete_document_sql:subModuleUIData?.folder_file_sql?.delete_document_sql
        };
        // console.log('deletePayload==>',deletePayload,subModuleUIData?.folder_file_sql?.delete_document_sql)
        const encryptedDeletePayload = EncryptUtils.encryptedData(deletePayload);
        await deleteFileCabinetDocumentData(
          'FileCabinet',
          'module',
          {tk: encryptedDeletePayload},
          'deleteFileCabinetDocument'
        );

        closeDialog();
        // Refresh the folder contents after successful
        const newRequestId = Date.now();
        latestRequestIdRef.current = newRequestId;
        await getDocumentFolders(newRequestId);
        enqueueSnackbar(
          'file deleted successfully.',
          SnackBarConfig.getSuccess()
        );
      }
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), SnackBarConfig.getError());
      }
    }
  };

  const hasActiveUploads = uploadProgress.some(item => item?.display);
  const firstSelectedFileMeta = getUploadFileMeta(uploadForm.files[0]);

  const isUploadButtonEnabled =
    dialogMode === 'create-file' &&
    !!uploadForm.documentDate?.trim() &&
    !!uploadForm.documentIdentifier1?.trim() &&
    uploadForm.files.length > 0 &&
    !hasActiveUploads;

  const isMetadataSaveEnabled =
    dialogMode === 'tag' && !!uploadForm.documentIdentifier1?.trim();

  const getDialogTitle = () => {
    switch (dialogMode) {
      case 'create-folder':
        return 'New Folder';
      case 'create-file':
        return 'Upload document';
      case 'edit':
        return 'Edit Item';
      case 'tag':
        return 'Edit Metadata';
      case 'delete':
        return 'Delete Item';
      default:
        return '';
    }
  };

  const renderDialogContent = () => {
    if (dialogMode === 'tag') {
      return (
        // <TextField
        //   autoFocus
        //   fullWidth
        //   label="Tags"
        //   placeholder="Enter comma separated tags"
        //   value={tagsInput}
        //   onChange={e => setTagsInput(e.target.value)}
        // />
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#e8fbf7',
                  color: '#18b7aa'
                }}>
                <ModeIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{fontWeight: 600}}>
                  Edit metadata
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Update document details - physical and logical names will be regenerated.
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<TuneOutlinedIcon />}
              sx={{textTransform: 'none', borderRadius: 2}}>
              Configure fields
            </Button>
          </Stack>

          <Divider />
         <Box
            sx={{
              maxHeight: '62vh',
              overflowY: 'auto',
              pr: 0.5,
              pt: 2,
              pb: 2
            }}>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {sm: '1fr', md: '1fr 1fr'},
                gap: 2,
                mb: 2
              }}>
          <TextField
            fullWidth
            label="DOCUMENT DATE"
            type="date"
            value={uploadForm.documentDate}
            onChange={handleUploadFormChange('documentDate')}
            InputLabelProps={{shrink: true}}
          />
          <TextField
            fullWidth
            label="OWNER"
            value={uploadForm.owner}
            onChange={handleUploadFormChange('owner')}
          />
          <TextField
            fullWidth
            label="VERIFIED BY"
            value={uploadForm.verifiedBy}
            onChange={handleUploadFormChange('verifiedBy')}
          />
          <TextField
            fullWidth
            label="REVIEWED BY"
            value={uploadForm.reviewedBy}
            onChange={handleUploadFormChange('reviewedBy')}
          />
          <TextField
            fullWidth
            label="SIGNED BY"
            value={uploadForm.signedBy}
            onChange={handleUploadFormChange('signedBy')}
          />
          <TextField
            fullWidth
            label="UPLOADED BY"
            value={currentUser?.name || ''}
            InputProps={{readOnly: true}}
            helperText="Auto-set to current logged-in user. Preserved on edit."
          />
          <TextField
            select
            fullWidth
            label="STATUS"
            value={uploadForm.status}
            onChange={handleUploadFormChange('status')}>
            <MenuItem value="Final">Final</MenuItem>
            <MenuItem value="Draft">Draft</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
          </TextField>
          <TextField
            fullWidth
            required
            label="DOCUMENT IDENTIFIER 1"
            value={uploadForm.documentIdentifier1}
            onChange={handleUploadFormChange('documentIdentifier1')}
            error={uploadFormErrors.documentIdentifier1}
            helperText={
              uploadFormErrors.documentIdentifier1
                ? 'Document identifier 1 is required'
                : 'Primary identifier for the source record'
            }
          />
          <TextField
            fullWidth
            label="DOCUMENT IDENTIFIER 2"
            value={uploadForm.documentIdentifier2}
            onChange={handleUploadFormChange('documentIdentifier2')}
            helperText="Secondary / patient identifier"
          />
          <TextField
            fullWidth
            label="DOCUMENT IDENTIFIER 3"
            value={uploadForm.documentIdentifier3}
            onChange={handleUploadFormChange('documentIdentifier3')}
            helperText="Order / encounter identifier"
          />
          <TextField
            fullWidth
            label="DOCUMENT IDENTIFIER 4"
            value={uploadForm.documentIdentifier4}
            onChange={handleUploadFormChange('documentIdentifier4')}
            helperText="Provider / facility identifier"
          />
          <TextField
            fullWidth
            label="DOCUMENT IDENTIFIER 5"
            value={uploadForm.documentIdentifier5}
            onChange={handleUploadFormChange('documentIdentifier5')}
            helperText="Reference / external identifier"
          />
        </Box>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              border: '1px solid #d9e2ec',
              backgroundColor: '#f8fafc',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                component="img"
                src={
                  getNodeType(activeItem) === 'dcm'
                    ? '/assets/icons/files/dcm.png'
                    : fileThumb(getNodeType(activeItem) || 'txt')
                }
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0
                }}
              />
              <Box sx={{minWidth: 0}}>
                <Typography
                  variant="caption"
                  sx={{display: 'block', color: '#6b7a90'}}>
                  Editing file
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 600,
                    wordBreak: 'break-word'
                  }}>
                  {activeItem?.Name || '-'}
                </Typography>
              </Box>
            </Stack>
            <Box sx={{textAlign: 'right', color: '#6b7a90'}}>
              <Typography variant="body2">
                {String(getNodeType(activeItem) || '')
                  .replace(/^\w/, char => char.toUpperCase()) || '-'}
              </Typography>
            </Box>
          </Box>
          <Box
              sx={{
                border: '1px solid #d9e2ec',
                borderRadius: 2,
                p: 2,
                backgroundColor: '#f8fafc'
              }}>
              <Typography
                variant="subtitle2"
                sx={{mb: 1.5, color: '#6b7a90', letterSpacing: '0.04em'}}>
                GENERATED NAMES (PREVIEW)
              </Typography>
              <Stack spacing={0.75}>
                {/* <Typography variant="body2">
                  {`Physical (Azure Blob): ${firstSelectedFileMeta?.name || '-'}`}
                </Typography> */}
                <Typography variant="body2">
                  {`Logical (UI label): ${
                              [
                                  uploadForm.documentIdentifier1,
                                  uploadForm.documentIdentifier2,
                                  uploadForm.documentIdentifier3,
                                  uploadForm.documentIdentifier4,
                                  uploadForm.documentIdentifier5
                                ]
                                  .filter(Boolean)
                                  .join('_') || '-'
                  }`}
                </Typography>
                <Typography variant="body2">
                  Source Document ID: <code>(auto-generated on upload)</code>
                </Typography>
                <Typography variant="body2">
                  Uploaded By: <code>{currentUser.name || ''}</code>
                </Typography>
                <Typography variant="body2">Version No: 1</Typography>
                <Typography variant="body2">
                  {`Document Date: ${uploadForm.documentDate || ''}`}
                </Typography>
              </Stack>
            </Box>
        </Box>
        </Stack>


      );
    }

    if (dialogMode === 'delete') {
      // console.log('activeItem-->',activeItem?.Name,activeItem.ID);
      return (
        <Typography variant="body2">
          Delete <strong>{activeItem?.Name}</strong> from this folder?
        </Typography>
      );
    }

    if (dialogMode === 'create-file') {
      return (
        <Stack spacing={2}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: '#e8fbf7',
                  color: '#18b7aa'
                }}>
                <UploadFileOutlinedIcon />
              </Box>
              <Box>
                <Typography variant="h6" sx={{fontWeight: 600}}>
                  Upload document
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Fill in document details first, then browse and attach your
                  file(s).
                </Typography>
              </Box>
            </Stack>
            <Button
              variant="outlined"
              startIcon={<TuneOutlinedIcon />}
              sx={{textTransform: 'none', borderRadius: 2}}>
              Configure fields
            </Button>
          </Stack>

          <Divider />

          <Box
            sx={{
              maxHeight: '62vh',
              overflowY: 'auto',
              pr: 0.5,
              pt: 2,
              pb: 2
            }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {xs: '1fr', md: '1fr 1fr'},
                gap: 2,
                mb: 2
              }}>
              <TextField
                fullWidth
                required
                label="DOCUMENT DATE"
                type="date"
                value={uploadForm.documentDate}
                onChange={handleUploadFormChange('documentDate')}
                InputLabelProps={{shrink: true}}
                error={uploadFormErrors.documentDate}
                helperText={
                  uploadFormErrors.documentDate
                    ? 'Document date is required'
                    : ''
                }
              />
              <TextField
                fullWidth
                label="OWNER"
                value={uploadForm.owner}
                onChange={handleUploadFormChange('owner')}
                helperText="Document owner"
              />
              <TextField
                fullWidth
                label="VERIFIED BY"
                value={uploadForm.verifiedBy}
                onChange={handleUploadFormChange('verifiedBy')}
              />
              <TextField
                fullWidth
                label="REVIEWED BY"
                value={uploadForm.reviewedBy}
                onChange={handleUploadFormChange('reviewedBy')}
              />
              <TextField
                fullWidth
                label="SIGNED BY"
                value={uploadForm.signedBy}
                onChange={handleUploadFormChange('signedBy')}
              />
              <TextField
                fullWidth
                label="UPLOADED BY"
                value={currentUser.name || ''}
                InputProps={{readOnly: true}}
                helperText="Auto-set to current logged-in user. Preserved on edit."
              />
              <TextField
                select
                fullWidth
                label="STATUS"
                value={uploadForm.status}
                onChange={handleUploadFormChange('status')}>
                <MenuItem value="Final">Final</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Pending">Pending</MenuItem>
              </TextField>
              <TextField
                fullWidth
                required
                label="DOCUMENT IDENTIFIER 1"
                value={uploadForm.documentIdentifier1}
                onChange={handleUploadFormChange('documentIdentifier1')}
                error={uploadFormErrors.documentIdentifier1}
                helperText={
                  uploadFormErrors.documentIdentifier1
                    ? 'Document identifier 1 is required'
                    : ''
                }
              />
              <TextField
                fullWidth
                label="DOCUMENT IDENTIFIER 2"
                value={uploadForm.documentIdentifier2}
                onChange={handleUploadFormChange('documentIdentifier2')}
                helperText="Secondary / patient identifier"
              />
              <TextField
                fullWidth
                label="DOCUMENT IDENTIFIER 3"
                value={uploadForm.documentIdentifier3}
                onChange={handleUploadFormChange('documentIdentifier3')}
                helperText="Order / encounter identifier"
              />
              <TextField
                fullWidth
                label="DOCUMENT IDENTIFIER 4"
                value={uploadForm.documentIdentifier4}
                onChange={handleUploadFormChange('documentIdentifier4')}
                helperText="Provider / facility identifier"
              />
              <TextField
                fullWidth
                label="DOCUMENT IDENTIFIER 5"
                value={uploadForm.documentIdentifier5}
                onChange={handleUploadFormChange('documentIdentifier5')}
                helperText="Reference / external identifier"
              />
            </Box>

            <Box
              sx={{
                border: '1px dashed #cfd8e3',
                borderRadius: 2,
                p: 2.5,
                mb: 2
              }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                mb={2}>
                <Typography
                  variant="subtitle2"
                  sx={{color: '#6b7a90', letterSpacing: '0.04em'}}>
                  ATTACH FILE(S)
                </Typography>
                <Button
                  variant="contained"
                  onClick={() => browseFilesInputRef.current?.click()}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    backgroundColor: '#209e93',
                    '&:hover': {backgroundColor: '#119d92'}
                  }}>
                  Browse Files
                </Button>
              </Stack>

              <input
                ref={browseFilesInputRef}
                type="file"
                multiple
                hidden
                onChange={handleBrowseFiles}
              />

              {uploadForm.files.length > 0 ? (
                 <Grid container spacing={2} sx={{mt: 2}}>
                <AnimatePresence initial={false}>
                  {uploadForm.files.length  &&
                    uploadForm.files.map((file: any, index) => {
                      const fileMeta = getUploadFileMeta(file);

                      if (!fileMeta) {
                        return (
                          <Grid
                            key={`invalid-file-${index}`}
                            item
                            xs={12}>
                            <Box
                              sx={{
                                border: '1px dashed #f5c2c7',
                                borderRadius: 1.5,
                                p: 1.5,
                                backgroundColor: '#fff5f5'
                              }}>
                              <Typography variant="body2" color="error">
                                This selected item is not a valid file and cannot be uploaded.
                              </Typography>
                            </Box>
                          </Grid>
                        );
                      }

                      const {
                        id,
                        name = '',
                        size = 0,
                        type = ''
                      } = fileMeta || {
                        id: '',
                        name: '',
                        type: '',
                        size: 0
                      };

                        const isNotFormatFile = typeof file === 'string';
                        const format = getFileFormatFromType(type);
                        return (
                          <Grid
                            key={id}
                            component={m.div}
                            item
                            xs={12}
                            md={10}
                            {...varFade().inUp}
                            sx={{
                              m: 0.5,
                              padding: 0.5,
                              borderRadius: 1.25,
                              overflow: 'hidden',
                              position: 'relative',
                              display: '-webkit-box',
                              paddingTop: '8px !important',
                              border: theme =>
                                `solid 1px ${alpha(theme.palette.grey[500], 0.16)}`
                            }}>
                            <Grid
                              alignItems="center"
                              justifyContent="center"
                              display="flex"
                              item
                              xs={12}
                              md={2}>
                              <Tooltip title={name}>
                                <Stack
                                  flexShrink={0}
                                  component="span"
                                  alignItems="center"
                                  justifyContent="center"
                                  display="flex"
                                  sx={{
                                    width: 'fit-content',
                                    height: 'inherit'
                                  }}>
                                  <Box
                                    component="img"
                                    src={
                                      format === 'dcm'
                                        ? '/assets/icons/files/dcm.png'
                                        : format
                                          ? fileThumb(format)
                                          : ''
                                    }
                                    sx={{
                                      width: 32,
                                      height: 32,
                                      flexShrink: 0
                                    }}
                                  />
                                </Stack>
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12} md={10}>
                              <ListItemText
                                sx={{padding: '1vh'}}
                                primary={
                                  <Typography
                                    variant="h4"
                                    noWrap
                                    sx={{
                                      textOverflow: 'ellipsis',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap'
                                    }}>
                                    {isNotFormatFile ? file : name}
                                  </Typography>
                                }
                                secondary={
                                  <Box>
                                    {uploadProgress &&
                                      uploadProgress.find(
                                        item => item.id === id && item.display
                                      ) && (
                                        <LinearProgress
                                          variant="determinate"
                                          value={
                                            uploadProgress.find(
                                              progress => progress.id === id
                                            )?.progress || 0
                                          }
                                          sx={{
                                            margin: '8px 0',
                                            borderRadius: '4px'
                                          }}
                                        />
                                      )}
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      sx={{
                                        display: 'block',
                                        textAlign: 'right'
                                      }}>
                                      {isNotFormatFile
                                        ? ''
                                        : `${size.toFixed(2)} MB`}
                                    </Typography>
                                  </Box>
                                }
                              />
                            </Grid>
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveSelectedFile(index)}
                              sx={{
                                p: 0.5,
                                top: 4,
                                right: 4,
                                position: 'absolute',
                                color: 'common.white',
                                bgcolor: theme =>
                                  alpha(theme.palette.grey[900], 0),
                                '&:hover': {
                                  bgcolor: theme =>
                                    alpha(theme.palette.grey[900], 0.72)
                                }
                              }}>
                              <Iconify icon="mingcute:close-line" width={14} />
                            </IconButton>
                          </Grid>
                        );
                      })}
                  </AnimatePresence>
                </Grid>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  spacing={1}
                  sx={{minHeight: 150, color: '#7a8699'}}>
                  <UploadFileOutlinedIcon sx={{fontSize: 42, opacity: 0.45}} />
                  <Typography variant="h6" sx={{fontSize: 16, fontWeight: 500}}>
                    No files selected yet.
                  </Typography>
                  <Typography variant="body2">
                    Click "Browse Files" to attach documents.
                  </Typography>
                </Stack>
              )}
            </Box>

            <Box
              sx={{
                border: '1px solid #d9e2ec',
                borderRadius: 2,
                p: 2,
                backgroundColor: '#f8fafc'
              }}>
              <Typography
                variant="subtitle2"
                sx={{mb: 1.5, color: '#6b7a90', letterSpacing: '0.04em'}}>
                GENERATED NAMES (PREVIEW)
              </Typography>
              <Stack spacing={0.75}>
                {/* <Typography variant="body2">
                  {`Physical (Azure Blob): ${firstSelectedFileMeta?.name || '-'}`}
                </Typography> */}
                <Typography variant="body2">
                  {`Logical (UI label): ${
                                                    [
                                  uploadForm.documentIdentifier1,
                                  uploadForm.documentIdentifier2,
                                  uploadForm.documentIdentifier3,
                                  uploadForm.documentIdentifier4,
                                  uploadForm.documentIdentifier5
                                ]
                                  .filter(Boolean)
                                  .join('_') || '-'
                  }`}
                </Typography>
                <Typography variant="body2">
                  Source Document ID: <code>(auto-generated on upload)</code>
                </Typography>
                <Typography variant="body2">
                  Uploaded By: <code>{currentUser.name || ''}</code>
                </Typography>
                <Typography variant="body2">Version No: 1</Typography>
                <Typography variant="body2">
                  {`Document Date: ${uploadForm.documentDate || '-'}`}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Stack>
      );
    }

    return (
      <TextField
        autoFocus
        fullWidth
        label="Name"
        placeholder="Enter name"
        value={nameInput}
        onChange={e => setNameInput(e.target.value)}
      />
    );
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          style: {
            minWidth: 400,
            padding: 0,
            display: 'flex',
            flexDirection: 'column'
          }
        }}
        MenuListProps={{
          disablePadding: true,
          sx: {flex: 1, display: 'flex', flexDirection: 'column', p: 0}
        }}>
        <Box
          sx={{
            px: 2,
            pt: 1,
            pb: 1,
            borderBottom: '1px solid #eee',
            background: '#fff',
            zIndex: 1
          }}>
          <TextField
            autoFocus
            placeholder="Search..."
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              setSearching(true);
            }}
            size="small"
            sx={{'&.MuiInputBase-root-MuiOutlinedInput-root': {pr: 0}}}
            fullWidth
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button>
                    <SearchIcon onClick={() => handleSearch()} />
                  </Button>
                </InputAdornment>
              )
            }}
          />
          <Stack direction="row" spacing={1} sx={{mt: 1.5}}>
            {canCreateFolder && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<CreateNewFolderOutlinedIcon />}
                onClick={() => openDialog('create-folder')}
                sx={{
                  textTransform: 'none',
                  borderColor: '#b8f1ed',
                  color: '#18b7aa',
                  backgroundColor: '#efffff'
                }}>
                New Folder
              </Button>
            )}
            {canCreateFile && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<NoteAddOutlinedIcon />}
                onClick={() => openDialog('create-file')}
                sx={{
                  textTransform: 'none',
                  borderColor: '#b8f1ed',
                  color: '#18b7aa',
                  backgroundColor: '#efffff'
                }}>
                New File
              </Button>
            )}
          </Stack>
        </Box>
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 80,
            maxHeight: '60vh',
            background: '#fff'
          }}>
          {isLoading ? (
            <MenuItem disabled>
              <CircularProgress size={20} sx={{marginRight: 1}} />
              Loading...
            </MenuItem>
          ) : pagedChildren.length > 0 ? (
            pagedChildren.map((child: any) => {
              const type = getNodeType(child);

              return (
                <MenuItem
                  key={child.ID}
                  onClick={() => handleMenuSelect(child)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                  <FileThumbnail
                    tooltip={true}
                    file={type || ''}
                    sx={{width: 15, height: 15}}
                  />
                  <Typography
                    noWrap
                    variant="inherit"
                    sx={{
                      flex: 1,
                      cursor: 'pointer',
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      color: theme.palette.common.black
                    }}>
                    {child.Name}
                  </Typography>
                  <Stack direction="row" spacing={0.25}>
                    {Actions && (
                      <Tooltip title="Rename">
                        <IconButton
                          size="small"
                          onClick={e => {
                            e.stopPropagation();
                            openDialog('edit', child);
                          }}>
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {Actions && type !== 'folder' && (
                      <Tooltip title="Edit metadata">
                        <IconButton
                          size="small"
                          color={
                            Array.isArray(child?.localTags) &&
                            child.localTags.length > 0
                              ? 'primary'
                              : 'default'
                          }
                          onClick={e => {
                            e.stopPropagation();
                            openDialog('tag', child);
                          }}>
                          <LocalOfferOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {Actions && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={e => {
                            e.stopPropagation();
                            openDialog('delete', child);
                          }}>
                          <DeleteOutlineOutlinedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </MenuItem>
              );
            })
          ) : (
            <MenuItem disabled>
              No files found for the selected folder and filters.
            </MenuItem>
          )}
        </Box>
        <Box
          sx={{
            px: 2,
            py: 1,
            borderTop: '1px solid #eee',
            background: '#fff',
            zIndex: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
          <Typography variant="caption">
            {pagedChildren.length === 0
              ? '0 results'
              : `${(page - 1) * pageSize + 1}-${Math.min(
                  page * pageSize,
                  childrensCount
                )} of ${childrensCount}`}
          </Typography>
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              variant="standard"
              sx={{width: 45}}>
              {pageSizeOptions.map(size => (
                <SelectMenuItem key={size} value={size}>
                  {size}
                </SelectMenuItem>
              ))}
            </Select>
            <Pagination
              count={pageCount}
              page={page}
              onChange={handlePageChange}
              size="small"
              color="primary"
              siblingCount={0}
              boundaryCount={1}
            />
          </Box>
        </Box>
      </Menu>
      <Dialog
        open={dialogMode !== null}
        onClose={closeDialog}
        fullWidth
        maxWidth={dialogMode === 'create-file' || dialogMode === 'tag' ? 'md' : 'xs'}>
        <DialogTitle
          sx={{
            display: dialogMode === 'create-file' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
          {getDialogTitle()}
        </DialogTitle>
        <DialogContent sx={{pt: '10px !important'}}>
          {renderDialogContent()}
        </DialogContent>
        <DialogActions
          sx={{
            px: dialogMode === 'create-file' ? 3 : 2,
            py: dialogMode === 'create-file' ? 2.5 : 1.5,
            borderTop:
              dialogMode === 'create-file' ? '1px solid #e5e7eb' : 'none'
          }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            variant="contained"
            color={dialogMode === 'delete' ? 'error' : 'primary'}
            startIcon={
              dialogMode === 'create-file' ? (
                <UploadFileOutlinedIcon />
              ) : undefined
            }
            disabled={
              dialogMode === 'create-file'
                ? !isUploadButtonEnabled
                : dialogMode === 'tag'
                  ? !isMetadataSaveEnabled
                : false
            }
            sx={
              dialogMode === 'create-file'
                ? {
                    textTransform: 'none',
                    borderRadius: 2,
                    px: 2.5,
                    backgroundColor: '#0dcfaf',
                    '&:hover': {backgroundColor: '#0dcfaf'}
                  }
                : undefined
            }
            onClick={handleDialogSubmit}>
            {dialogMode === 'delete'
              ? 'Delete'
              : dialogMode === 'create-file'
                ? `Upload ${!uploadForm.files?.length || uploadForm.files.length === 0 ? 'a file' : `${uploadForm.files.length} files`}`
                : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MenuWithSearch;
